import {
  IconHome,
  IconMap2,
  IconSettings,
  IconHelp,
  IconFileText,
  IconArrowsLeftRight,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';

export interface NavItem {
  id: string;
  label: string;
  href?: string;
  ariaLabel?: string;
  Icon?: React.ComponentType<{ size?: number }>;
  children?: NavItem[];
}

// Default static nav items (fallback / tests)
export const navItems: NavItem[] = [
  { id: 'home', label: 'Inicio', href: '/', ariaLabel: 'Ir al inicio', Icon: IconHome },
  {
    id: 'trips',
    label: 'Viajes',
    ariaLabel: 'Ver viajes',
    Icon: IconMap2,
    // Grouped children: keep original trips and the convenio page as submenu entries
    children: [
      {
        id: 'trips-pacto',
        label: 'Viajes (pacto)',
        href: '/trips/pacto',
        ariaLabel: 'Ver viajes (pacto)',
        Icon: IconMap2,
      },
      {
        id: 'trips-empresa',
        label: 'Empresa',
        href: '/trips/empresa',
        ariaLabel: 'Ver viajes empresa',
        Icon: IconFileText,
      },
      {
        id: 'trips-convenio',
        label: 'Convenio',
        href: '/trips/convenio',
        ariaLabel: 'Ver viajes convenio',
        Icon: IconFileText,
      },
      {
        id: 'trips-compare',
        label: 'Comparativa',
        href: '/trips/comparativa',
        ariaLabel: 'Ver comparativa de cálculos',
        Icon: IconArrowsLeftRight,
      },
    ],
  },
  {
    id: 'presencia',
    label: 'Presencia',
    href: '/presencia',
    ariaLabel: 'Calculadora de presencia',
    Icon: IconFileText,
  },
  {
    id: 'settings',
    label: 'Ajustes',
    href: '/settings',
    ariaLabel: 'Ajustes de la aplicación',
    Icon: IconSettings,
  },
  { id: 'help', label: 'Ayuda', href: '/help', ariaLabel: 'Centro de ayuda', Icon: IconHelp },
];

// Factory: returns runtime nav items given a localized strings object L
export function getNavItems(): NavItem[] {
  return [
    { id: 'home', label: 'Inicio', href: '/', ariaLabel: 'Ir al inicio', Icon: IconHome },
    {
      id: 'trips',
      label: 'Viajes',
      ariaLabel: 'Ver viajes',
      Icon: IconMap2,
      children: [
        {
          id: 'trips-pacto',
          label: 'Viajes (pacto)',
          href: '/trips/pacto',
          ariaLabel: 'Ver viajes (pacto)',
          Icon: IconMap2,
        },
        {
          id: 'trips-empresa',
          label: 'Empresa',
          href: '/trips/empresa',
          ariaLabel: 'Ver viajes empresa',
          Icon: IconMap2,
        },
        {
          id: 'trips-compare',
          label: 'Comparativa de cálculos',
          href: '/trips/comparativa',
          ariaLabel: 'Ver comparativa de cálculos',
          Icon: IconArrowsLeftRight,
        },
      ],
    },
    {
      id: 'presencia',
      label: 'Presencia',
      href: '/presencia',
      ariaLabel: 'Calculadora de presencia',
      Icon: IconFileText,
    },
    {
      id: 'settings',
      label: 'Ajustes',
      href: '/settings',
      ariaLabel: 'Ajustes de la aplicación',
      Icon: IconSettings,
    },
    { id: 'help', label: 'Ayuda', href: '/help', ariaLabel: 'Centro de ayuda', Icon: IconHelp },
  ];
}

// Helper for runtime localized nav items when using react-i18next. Accepts a `t` function.
export function getNavItemsWithT(t: TFunction): NavItem[] {
  return [
    {
      id: 'home',
      label: t('inicio'),
      href: '/',
      ariaLabel: `${t('irA')} ${t('inicio').toLowerCase()}`,
      Icon: IconHome,
    },
    {
      id: 'trips',
      label: t('viajes'),
      ariaLabel: t('verViajes'),
      Icon: IconMap2,
      children: [
        {
          id: 'trips-pacto',
          label: t('viajesPacto') ?? 'Pacto',
          href: '/trips/pacto',
          ariaLabel: t('verViajesPacto'),
          Icon: IconMap2,
        },
        {
          id: 'trips-convenio',
          label: t('viajesEmpresa'),
          href: '/trips/empresa',
          ariaLabel: t('verViajesEmpresa'),
          Icon: IconMap2,
        },
        {
          id: 'trips-compare',
          label: t('comparativaCalculos'),
          href: '/trips/comparativa',
          ariaLabel: t('verComparativaCalculos'),
          Icon: IconArrowsLeftRight,
        },
      ],
    },
    {
      id: 'presencia',
      label: t('presencia') ?? 'Presencia',
      href: '/presencia',
      ariaLabel: t('presencia') ?? 'Presencia',
      Icon: IconFileText,
    },
    {
      id: 'settings',
      label: t('ajustes'),
      href: '/settings',
      ariaLabel: t('ajustesAplicacion'),
      Icon: IconSettings,
    },
    {
      id: 'help',
      label: t('ayuda'),
      href: '/help',
      ariaLabel: t('centroAyuda'),
      Icon: IconHelp,
    },
  ];
}
